import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaUsers, FaTrophy, FaChartBar, FaThList, FaSearch,
    FaFilter, FaGraduationCap, FaSchool, FaDownload, FaEdit, FaTimes,
    FaArrowLeft, FaArrowRight
} from 'react-icons/fa';
import { fetchAPI } from '../../utils/api';
import { showSuccess, showError } from '../../utils/toastUtils';
import * as XLSX from 'xlsx';

const ITEMS_PER_PAGE = 10;

export default function AdminEvaluation() {
    const [activeTab, setActiveTab] = useState('name-list');
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [data, setData] = useState({
        students: [],
        exams: [],
        summary: {},
        grade_totals: []
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [filterGrade, setFilterGrade] = useState('All');
    const [filterInstitution, setFilterInstitution] = useState('All');
    const [filterMedium, setFilterMedium] = useState('All');
    const [selectedExamId, setSelectedExamId] = useState('Average');
    const [editingExam, setEditingExam] = useState(null);
    const [isEditExamModalOpen, setIsEditExamModalOpen] = useState(false);
    const [editExamForm, setEditExamForm] = useState({ name: '', exam_date: '' });
    const [isUpdating, setIsUpdating] = useState(false);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterGrade, filterInstitution, filterMedium, activeTab, selectedExamId]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const result = await fetchAPI('/admin/get_evaluation_data.php');
            setData(result);
        } catch (err) {
            showError("Failed to load evaluation data: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateExam = async (e) => {
        e.preventDefault();
        setIsUpdating(true);
        try {
            const res = await fetchAPI('/admin/update_exam.php', {
                method: 'POST',
                body: JSON.stringify({
                    exam_id: editingExam.id,
                    ...editExamForm
                })
            });
            if (res.status === 'success') {
                showSuccess('Exam updated successfully');
                setIsEditExamModalOpen(false);
                loadData();
            } else {
                showError(res.message);
            }
        } catch (error) {
            showError('Failed to update exam');
        } finally {
            setIsUpdating(false);
        }
    };

    const openEditExamModal = (exam) => {
        setEditingExam(exam);
        setEditExamForm({ name: exam.name, exam_date: exam.exam_date });
        setIsEditExamModalOpen(true);
    };


    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(filteredStudents.map(s => {
            const row = {
                'Name': s.name,
                'Index': s.index_number,
                'Grade': s.grade,
                'Medium': s.medium || 'Sinhala',
                'Institution': s.institution,
                'School': s.school_name
            };
            data.exams.forEach(exam => {
                row[exam.name] = s.marks[exam.id] !== undefined ? s.marks[exam.id] : 'AB';
            });
            return row;
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Evaluation");
        XLSX.writeFile(wb, "Student_Evaluation.xlsx");
        showSuccess("Exported to Excel!");
    };

    const filteredStudents = data.students.filter(s => {
        const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (s.index_number && s.index_number.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesGrade = filterGrade === 'All' || s.grade?.toString() === filterGrade;
        const matchesInst = filterInstitution === 'All' || s.institution === filterInstitution;
        const matchesMedium = filterMedium === 'All' || s.medium === filterMedium;
        return matchesSearch && matchesGrade && matchesInst && matchesMedium;
    });

    const uniqueGrades = ['All', ...new Set(data.students.map(s => s.grade?.toString()).filter(Boolean))].sort((a, b) => (a === 'All' ? -1 : b === 'All' ? 1 : parseInt(a) - parseInt(b)));
    const uniqueInstitutions = ['All', ...new Set(data.students.map(s => s.institution).filter(Boolean))].sort();

    // Group exams by month
    const groupedExams = data.exams.reduce((acc, exam) => {
        const date = new Date(exam.exam_date);
        const monthString = date.toLocaleString('default', { month: 'long' });
        const month = monthString.charAt(0).toUpperCase() + monthString.slice(1); // Capitalize
        if (!acc[month]) acc[month] = [];
        acc[month].push(exam);
        return acc;
    }, {});

    const monthColors = {
        'January': 'bg-blue-500', 'February': 'bg-indigo-500', 'March': 'bg-cyan-500',
        'April': 'bg-teal-500', 'May': 'bg-emerald-500', 'June': 'bg-green-500',
        'July': 'bg-lime-500', 'August': 'bg-yellow-500', 'September': 'bg-orange-500',
        'October': 'bg-red-500', 'November': 'bg-rose-500', 'December': 'bg-pink-500'
    };

    // Calculate rankings for filtered students
    const rankedStudents = [...filteredStudents].map(s => {
        let score = 0;
        if (selectedExamId === 'Average') {
            const marks = Object.values(s.marks);
            score = marks.length > 0 ? (marks.reduce((a, b) => a + b, 0) / marks.length).toFixed(1) : 0;
        } else {
            score = s.marks[selectedExamId] !== undefined ? s.marks[selectedExamId] : 0;
        }
        return { ...s, score: parseFloat(score) };
    }).sort((a, b) => b.score - a.score);

    // Pagination Logic (Calculated after data is filtered and ranked)
    const getPaginatedData = (items) => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return items.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    };

    const paginatedNameList = getPaginatedData(filteredStudents);
    const paginatedRankings = getPaginatedData(rankedStudents);

    const Pagination = ({ totalItems }) => {
        const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
        if (totalPages <= 1) return null;

        return (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 bg-white/40 backdrop-blur-md p-4 rounded-lg border border-white/50 shadow-sm">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                    Showing <span className="text-blue-600">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="text-blue-600">{Math.min(currentPage * ITEMS_PER_PAGE, totalItems)}</span> of {totalItems} students
                </p>
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg bg-white border border-gray-100 text-gray-400 hover:text-blue-600 disabled:opacity-30 transition-all shadow-sm active:scale-90"
                    >
                        <FaArrowLeft size={12} />
                    </button>
                    {[...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        // Simple logic to show only surrounding pages if many
                        if (totalPages > 7 && Math.abs(pageNum - currentPage) > 2 && pageNum !== 1 && pageNum !== totalPages) {
                            if (Math.abs(pageNum - currentPage) === 3) return <span key={pageNum} className="text-gray-300">...</span>;
                            return null;
                        }

                        return (
                            <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`w-8 h-8 rounded-lg text-[11px] font-bold transition-all shadow-sm active:scale-95 ${currentPage === pageNum
                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                                    : 'bg-white text-gray-500 hover:text-blue-600 border border-gray-50'
                                    }`}
                            >
                                {pageNum}
                            </button>
                        );
                    })}
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg bg-white border border-gray-100 text-gray-400 hover:text-blue-600 disabled:opacity-30 transition-all shadow-sm active:scale-90"
                    >
                        <FaArrowRight size={12} />
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    return (
        <div className="space-y-6 pb-20 max-w-[1600px] mx-auto">
            {/* Header Section */}
            <div className="bg-white/60 backdrop-blur-xl border border-white/50 p-8 rounded-lg shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight font-display">Student Evaluation process</h1>
                    <p className="text-gray-500 font-medium">Manage and evaluate student performance across institutions.</p>
                </div>

                <div className="flex flex-wrap gap-2 p-1.5 bg-gray-100/50 rounded-lg border border-gray-100">
                    {[
                        { id: 'name-list', label: 'Name List', icon: FaThList, color: 'bg-red-500' },
                        { id: 'rankings', label: 'Rankings', icon: FaTrophy, color: 'bg-indigo-600' },
                        { id: 'summary', label: 'Summary', icon: FaChartBar, color: 'bg-cyan-500' },
                        { id: 'grade-totals', label: 'Grade Wise', icon: FaUsers, color: 'bg-purple-600' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-xs uppercase tracking-widest transition-all ${activeTab === tab.id
                                ? `${tab.color} text-white shadow-xl ${tab.color}/20`
                                : 'text-gray-500 hover:bg-white hover:text-gray-900'
                                }`}
                        >
                            <tab.icon className={activeTab === tab.id ? '' : 'text-gray-400'} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Filters Section */}
            {(activeTab === 'name-list' || activeTab === 'rankings') && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="relative group col-span-1 md:col-span-2">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name or index..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-white border border-gray-100 rounded-lg py-4 pl-12 pr-4 font-semibold text-gray-700 focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                        />
                    </div>
                    <div className="relative">
                        <FaGraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <select
                            value={filterGrade}
                            onChange={(e) => setFilterGrade(e.target.value)}
                            className="w-full bg-white border border-gray-100 rounded-lg py-4 pl-12 pr-4 font-semibold text-gray-700 focus:ring-4 focus:ring-blue-500/5 outline-none appearance-none"
                        >
                            {uniqueGrades.map(g => <option key={g} value={g}>{g === 'All' ? 'All Grades' : `Grade ${g}`}</option>)}
                        </select>
                    </div>
                    <div className="relative">
                        <FaSchool className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <select
                            value={filterInstitution}
                            onChange={(e) => setFilterInstitution(e.target.value)}
                            className="w-full bg-white border border-gray-100 rounded-lg py-4 pl-12 pr-4 font-semibold text-gray-700 focus:ring-4 focus:ring-blue-500/5 outline-none appearance-none"
                        >
                            {uniqueInstitutions.map(inst => <option key={inst} value={inst}>{inst === 'All' ? 'All Institutions' : inst}</option>)}
                        </select>
                    </div>
                    <div className="relative">
                        <FaFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <select
                            value={filterMedium}
                            onChange={(e) => setFilterMedium(e.target.value)}
                            className="w-full bg-white border border-gray-100 rounded-lg py-4 pl-12 pr-4 font-semibold text-gray-700 focus:ring-4 focus:ring-blue-500/5 outline-none appearance-none"
                        >
                            <option value="All">All Mediums</option>
                            <option value="Sinhala">Sinhala Medium</option>
                            <option value="English">English Medium</option>
                        </select>
                    </div>
                    {activeTab === 'rankings' && (
                        <div className="relative">
                            <FaChartBar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <select
                                value={selectedExamId}
                                onChange={(e) => setSelectedExamId(e.target.value)}
                                className="w-full bg-white border border-gray-100 rounded-lg py-4 pl-12 pr-4 font-semibold text-gray-700 focus:ring-4 focus:ring-blue-500/5 outline-none appearance-none"
                            >
                                <option value="Average">Overall Average Ranking</option>
                                {data.exams.map(exam => (
                                    <option key={exam.id} value={exam.id}>{exam.name} Ranking</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            )}

            {/* Main Content Area */}
            <div className="bg-white rounded-lg shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden min-h-[500px]">
                <AnimatePresence mode="wait">
                    {activeTab === 'name-list' && (
                        <motion.div
                            key="name-list"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="p-8"
                        >
                            <div className="overflow-x-auto pb-4">
                                <table className="w-full border-collapse rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                                    <thead>
                                        {/* Month Headers */}
                                        <tr className="border-b border-gray-200">
                                            <th colSpan={6} className="bg-gray-50/50"></th>
                                            {Object.entries(groupedExams).map(([month, exams]) => (
                                                <th
                                                    key={month}
                                                    colSpan={exams.length}
                                                    className={`p-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white text-center border-l border-white/20 ${monthColors[month] || 'bg-gray-600'}`}
                                                >
                                                    {month}
                                                </th>
                                            ))}
                                            <th className="bg-blue-600 text-white p-3 text-[10px] font-bold uppercase tracking-[0.2em] border-l border-white/20 rounded-tr-lg">Summary</th>
                                        </tr>
                                        {/* Main Headers */}
                                        <tr className="bg-white text-gray-900 border-b border-gray-200 shadow-sm relative z-10">
                                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-left sticky left-0 bg-white border-r border-gray-100 z-20">Institution</th>
                                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-left sticky left-[120px] bg-white border-r border-gray-100 z-20">SM Admin</th>
                                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-left sticky left-[220px] bg-white border-r border-gray-100 z-20">Name</th>
                                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-center border-r border-gray-100">Med</th>
                                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-center border-r border-gray-100">Grad</th>
                                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-left border-r border-gray-100">School</th>
                                            {Object.values(groupedExams).flat().map(exam => (
                                                <th key={exam.id} className="p-4 text-[10px] font-bold uppercase tracking-widest text-center min-w-[120px] border-r border-gray-100 bg-gray-50/30 group/exam relative">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span>{exam.name}</span>
                                                        <button
                                                            onClick={() => openEditExamModal(exam)}
                                                            className="opacity-0 group-hover/exam:opacity-100 transition-opacity p-1 hover:bg-blue-100 rounded text-blue-600"
                                                            title="Edit Exam Name"
                                                        >
                                                            <FaEdit size={10} />
                                                        </button>
                                                    </div>
                                                </th>
                                            ))}
                                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-center bg-blue-50 text-blue-700">Avg</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {paginatedNameList.map((s, idx) => {
                                            const marksValues = Object.values(s.marks);
                                            const avg = marksValues.length > 0
                                                ? (marksValues.reduce((a, b) => a + b, 0) / marksValues.length).toFixed(1)
                                                : '-';
                                            return (
                                                <tr key={s.id} className="hover:bg-blue-50/40 transition-colors group">
                                                    <td className="p-4 text-xs font-semibold text-gray-600 border-r border-gray-50 whitespace-nowrap sticky left-0 bg-inherit z-10">{s.institution}</td>
                                                    <td className="p-4 text-xs font-bold text-blue-600 border-r border-gray-50 whitespace-nowrap sticky left-[120px] bg-inherit z-10">{s.index_number}</td>
                                                    <td className="p-4 text-[13px] font-bold text-gray-900 border-r border-gray-50 whitespace-nowrap sticky left-[220px] bg-inherit z-10">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 uppercase group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                                                {s.name.charAt(0)}
                                                            </div>
                                                            {s.name}
                                                        </div>
                                                    </td>
                                                    <td className="p-4 text-xs font-bold text-gray-500 text-center border-r border-gray-50">
                                                        {s.medium === 'English' ? 'EN' : 'SI'}
                                                    </td>
                                                    <td className="p-4 text-xs font-bold text-gray-700 text-center border-r border-gray-50">{s.grade}</td>
                                                    <td className="p-4 text-[11px] font-semibold text-gray-400 max-w-[180px] truncate border-r border-gray-50">{s.school_name}</td>
                                                    {Object.values(groupedExams).flat().map(exam => (
                                                        <td key={exam.id} className="p-4 text-xs font-bold text-center border-r border-gray-50">
                                                            {s.marks[exam.id] !== undefined ? (
                                                                <div className={`inline-flex items-center justify-center w-10 h-8 rounded-md font-semibold ${s.marks[exam.id] >= 75 ? 'bg-green-100 text-green-700' :
                                                                    s.marks[exam.id] >= 40 ? 'bg-blue-100 text-blue-700' :
                                                                        'bg-red-100 text-red-700'
                                                                    }`}>
                                                                    {s.marks[exam.id]}
                                                                </div>
                                                            ) : (
                                                                <span className="text-red-400 font-bold">AB</span>
                                                            )}
                                                        </td>
                                                    ))}
                                                    <td className="p-4 text-sm font-bold text-blue-700 text-center bg-blue-50/30">
                                                        {avg !== '-' ? (
                                                            <div className="bg-blue-600 text-white py-1 px-3 rounded-md text-xs shadow-md shadow-blue-200">
                                                                {avg}%
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                <Pagination totalItems={filteredStudents.length} />

                                {filteredStudents.length === 0 && (
                                    <div className="py-20 text-center">
                                        <FaUsers className="text-5xl text-gray-200 mx-auto mb-4" />
                                        <p className="text-gray-400 font-semibold uppercase tracking-widest">No students found</p>
                                    </div>
                                )}
                            </div>
                            <div className="mt-8 flex justify-end">
                                <button
                                    onClick={exportToExcel}
                                    className="flex items-center gap-2 px-8 py-4 bg-green-600 text-white rounded-lg font-bold text-xs uppercase tracking-[0.2em] transition-all hover:bg-green-700 active:scale-95 shadow-xl shadow-green-200"
                                >
                                    <FaDownload /> Export Name List
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'rankings' && (
                        <motion.div
                            key="rankings"
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 1.02 }}
                            className="p-8"
                        >
                            <div className="overflow-x-auto rounded-lg border border-white/50 bg-white/40 backdrop-blur-md shadow-sm">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-900/90 backdrop-blur-md text-white">
                                            <th className="p-5 text-[10px] font-bold uppercase tracking-widest text-center w-20">Rank</th>
                                            <th className="p-5 text-[10px] font-bold uppercase tracking-widest text-left">Student Details</th>
                                            <th className="p-5 text-[10px] font-bold uppercase tracking-widest text-center w-24">Grade</th>
                                            <th className="p-5 text-[10px] font-bold uppercase tracking-widest text-left">Institution / School</th>
                                            <th className="p-5 text-[10px] font-bold uppercase tracking-widest text-center w-32">
                                                {selectedExamId === 'Average' ? 'Avg Mark' : 'Marks'}
                                            </th>
                                            <th className="p-5 text-[10px] font-bold uppercase tracking-widest text-center w-24">%</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {paginatedRankings.map((s, idx) => {
                                            // Tie handling for rank display
                                            let currentRank = ((currentPage - 1) * ITEMS_PER_PAGE) + idx + 1;
                                            if (idx > 0 && paginatedRankings[idx - 1].score === s.score) {
                                                // Find the first index with this score in ALL rankings
                                                const firstTieIdx = rankedStudents.findIndex(rs => rs.score === s.score);
                                                currentRank = firstTieIdx + 1;
                                            }

                                            return (
                                                <tr key={s.id} className="hover:bg-blue-50/50 transition-colors group">
                                                    <td className="p-4 text-center">
                                                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg font-bold text-lg ${currentRank === 1 ? 'bg-yellow-400 text-yellow-900 shadow-lg shadow-yellow-200' :
                                                            currentRank === 2 ? 'bg-gray-200 text-gray-700' :
                                                                currentRank === 3 ? 'bg-orange-400 text-orange-900' :
                                                                    'bg-gray-50 text-gray-400'
                                                            }`}>
                                                            {currentRank}
                                                        </span>
                                                    </td>
                                                    <td className="p-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 uppercase group-hover:bg-blue-600 group-hover:text-white transition-all">
                                                                {s.name.charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div className="text-sm font-bold text-gray-900">{s.name}</div>
                                                                <div className="text-[10px] font-semibold text-blue-500 uppercase tracking-widest">{s.index_number}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        <div className="flex flex-col items-center">
                                                            <span className="text-sm font-bold text-gray-700">Grade {s.grade}</span>
                                                            <span className={`text-[9px] font-black uppercase tracking-tighter mt-1 px-1.5 py-0.5 rounded ${s.medium === 'English' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                                                {s.medium || 'Sinhala'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="p-4">
                                                        <div className="flex flex-col">
                                                            <span className="text-[11px] font-bold text-blue-600 uppercase tracking-tight mb-0.5">{s.institution}</span>
                                                            <span className="text-xs font-semibold text-gray-400 max-w-[200px] truncate">{s.school_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        <span className={`text-base font-bold ${s.score >= 75 ? 'text-green-600' : s.score >= 40 ? 'text-blue-600' : 'text-red-500'}`}>
                                                            {s.score}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        <div className="inline-flex items-center justify-center px-4 py-1 rounded-full bg-gray-100 text-[11px] font-bold text-gray-900">
                                                            {s.score}%
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                <Pagination totalItems={rankedStudents.length} />
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'summary' && (
                        <motion.div
                            key="summary"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="p-8"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {Object.entries(data.summary).map(([inst, stats]) => (
                                    <div key={inst} className="bg-white/60 backdrop-blur-xl border border-white/50 rounded-lg p-8 shadow-sm hover:shadow-xl hover:shadow-blue-500/5 transition-all group">
                                        <div className="flex justify-between items-start mb-8 pb-6 border-b border-gray-100">
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors uppercase tracking-tight">{inst}</h3>
                                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.2em] mt-1">Institution Summary</p>
                                            </div>
                                            <div className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg shadow-blue-200">
                                                <span className="text-2xl font-bold">{stats.total}</span>
                                                <p className="text-[8px] font-bold uppercase tracking-tighter opacity-80">Students</p>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            {Object.entries(stats.grades).sort((a, b) => a[0] - b[0]).map(([grade, count]) => (
                                                <div key={grade} className="flex items-center justify-between p-4 bg-white/40 rounded-lg border border-white group/item hover:bg-white hover:border-blue-200 transition-all">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-sm group-hover/item:bg-blue-600 group-hover/item:text-white transition-all">
                                                            {grade}
                                                        </div>
                                                        <span className="font-semibold text-gray-600 text-sm">Grade {grade}</span>
                                                    </div>
                                                    <span className="font-bold text-gray-900 text-lg">{count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'grade-totals' && (
                        <motion.div
                            key="grade-totals"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 1.05 }}
                            className="p-12 max-w-4xl mx-auto"
                        >
                            <div className="bg-white/60 backdrop-blur-2xl border border-white/50 rounded-lg p-12 shadow-2xl relative overflow-hidden">
                                {/* Decorative Blobs */}
                                <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl animate-blob"></div>
                                <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl animate-blob animation-delay-2000"></div>

                                <div className="flex items-center gap-6 mb-12 pb-6 border-b border-gray-100 relative z-10">
                                    <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-xl shadow-blue-200">
                                        <FaGraduationCap size={32} />
                                    </div>
                                    <div>
                                        <h3 className="text-3xl font-bold text-gray-900 tracking-tight font-display">Grade Wise Distribution</h3>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-[0.3em] mt-1">Total Student Census</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                                    {data.grade_totals.map((gt, idx) => (
                                        <motion.div
                                            key={gt.grade}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.05 }}
                                            className="group flex flex-col p-6 rounded-lg bg-white/40 border border-white/50 hover:bg-white hover:border-blue-100 transition-all shadow-sm active:scale-95"
                                        >
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs uppercase group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                                        G{gt.grade}
                                                    </div>
                                                    <span className="font-bold text-gray-700">Grade {gt.grade}</span>
                                                </div>
                                                <span className="text-2xl font-bold text-gray-900">{gt.qty}</span>
                                            </div>
                                            <div className="mt-4 w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${(gt.qty / data.grade_totals.reduce((a, b) => a + b.qty, 0)) * 100}%` }}
                                                    className="h-full bg-blue-500 rounded-full"
                                                ></motion.div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                <div className="mt-12 p-8 rounded-lg bg-gray-900 shadow-2xl relative z-10 flex justify-between items-center">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center text-blue-400">
                                            <FaUsers size={24} />
                                        </div>
                                        <div>
                                            <span className="text-white/50 text-[10px] font-bold uppercase tracking-[0.3em]">System Total</span>
                                            <h4 className="text-2xl font-bold text-white tracking-tight">Active Student Base</h4>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-5xl font-bold text-white tracking-tighter">
                                            {data.grade_totals.reduce((a, b) => a + b.qty, 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            {/* Edit Exam Modal */}
            <AnimatePresence>
                {isEditExamModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden border border-white/20"
                        >
                            <div className="bg-gray-900 p-6 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-bold text-white tracking-tight">Edit Exam Details</h3>
                                    <p className="text-blue-400 text-[10px] font-bold uppercase tracking-widest mt-0.5">Update Exam Info</p>
                                </div>
                                <button
                                    onClick={() => setIsEditExamModalOpen(false)}
                                    className="text-white/50 hover:text-white transition-colors"
                                >
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleUpdateExam} className="p-8 space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Exam Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={editExamForm.name}
                                        onChange={(e) => setEditExamForm({ ...editExamForm, name: e.target.value })}
                                        className="w-full bg-gray-50 border border-gray-100 rounded-lg py-4 px-5 text-sm font-semibold text-gray-700 focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none transition-all"
                                        placeholder="Enter exam name..."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Exam Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={editExamForm.exam_date}
                                        onChange={(e) => setEditExamForm({ ...editExamForm, exam_date: e.target.value })}
                                        className="w-full bg-gray-50 border border-gray-100 rounded-lg py-4 px-5 text-sm font-semibold text-gray-700 focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none transition-all"
                                    />
                                </div>

                                <div className="pt-4 flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsEditExamModalOpen(false)}
                                        className="flex-1 px-6 py-4 rounded-lg font-bold text-xs uppercase tracking-widest text-gray-500 bg-gray-50 hover:bg-gray-100 transition-all border border-gray-100"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isUpdating}
                                        className="flex-1 px-6 py-4 rounded-lg font-bold text-xs uppercase tracking-widest text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-xl shadow-blue-200 active:scale-95 disabled:opacity-50"
                                    >
                                        {isUpdating ? 'Updating...' : 'Update Details'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
